import { Button, Dialog, Field, Input } from "@dl/web-core-js/ui";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { OverrideNeeded } from "../../api/floor";

/** What the person going past the limit says: why, and at a kiosk, whose badge allows it. */
export interface OverrideAnswer {
  reason: string;
  /** A lead's badge scanned for this one report; null keeps the badge already in use. */
  badge: string | null;
}

/**
 * Asks for an override when a report goes past what the step's dependencies
 * allow (409 OVERRIDE). `ask` opens the dialog and settles with the answer, or
 * null when it is cancelled, so a mutation can await it and resend.
 *
 * <p>At a kiosk the badge in use must be allowed to override; when it is not
 * (`needsBadge`), the dialog asks for a lead's badge as well, used for that
 * one report only.</p>
 */
export function useOverridePrompt(needsBadge: boolean): { ask: (needed: OverrideNeeded) => Promise<OverrideAnswer | null>; element: ReactNode } {
  const [pending, setPending] = useState<{ needed: OverrideNeeded; resolve: (answer: OverrideAnswer | null) => void } | null>(null);
  const ask = (needed: OverrideNeeded) => new Promise<OverrideAnswer | null>((resolve) => setPending({ needed, resolve }));
  const settle = (answer: OverrideAnswer | null) => {
    pending?.resolve(answer);
    setPending(null);
  };
  const element = pending ? <OverrideDialog needed={pending.needed} needsBadge={needsBadge} onCancel={() => settle(null)} onSubmit={settle} /> : null;
  return { ask, element };
}

function OverrideDialog({
  needed,
  needsBadge,
  onCancel,
  onSubmit,
}: {
  needed: OverrideNeeded;
  needsBadge: boolean;
  onCancel: () => void;
  onSubmit: (answer: OverrideAnswer) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [badge, setBadge] = useState("");
  const valid = reason.trim() !== "" && (!needsBadge || badge.trim() !== "");
  const submit = () => {
    if (valid) onSubmit({ reason: reason.trim(), badge: needsBadge ? badge.trim() : null });
  };
  return (
    <Dialog
      open
      size="md"
      onClose={onCancel}
      title={t("override.title", "Past what the steps before allow")}
      description={needed.message}
      actions={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} onClick={submit}>
            {t("override.record", "Record it anyway")}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <p className="text-[13px] text-ink-muted">
          {t("override.explain", "It is recorded with the reason, who did it and by how much, so the count can always be explained.")}
        </p>
        <Field label={t("override.reason", "Why")} htmlFor="override-reason" required hint={t("override.reason-hint", "A part from another lot, rework, a miscount upstream.")}>
          <Input id="override-reason" autoFocus value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        {needsBadge && (
          <Field
            label={t("override.badge", "Lead's badge")}
            htmlFor="override-badge"
            required
            hint={t("override.badge-hint", "This badge cannot override: scan the badge of someone who can.")}
          >
            <Input id="override-badge" value={badge} onChange={(event) => setBadge(event.target.value)} />
          </Field>
        )}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
